import React, { Fragment, useCallback, useEffect, useState } from "react";
import { Button, message, Modal, Row, Spin, Tree, Typography } from "antd";
import {
  DeleteOutlined,
  DownOutlined,
  EditOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { useAppDispatch, useAppSelector } from "../../../application/store";
import { loadCategories } from "../../../application/categories-slice";
import { ServiceLocater, Services } from "../../../service-locater";
import {
  CategoriesFailure,
  CategoriesRepository,
  Either,
  left,
} from "../../../domain";
import I18n from "../../i18n";
import NewCategoryModal from "./components/NewCategoryModal";
import EditCategoryModal from "./components/EditCategoryModal";

type CategoryType = "category" | "subCategory" | "generalCategory";

export default function Categories() {
  const dispatch = useAppDispatch();
  const { loading, categories } = useAppSelector((state) => state.categories);

  const stableDispatch = useCallback(dispatch, []);
  useEffect(() => {
    if (loading !== "succeeded") stableDispatch(loadCategories());
  }, [stableDispatch, loading]);

  const [newCategoryModalVisible, setNewCategoryModalVisible] = useState<{
    parentId?: string;
    categoryType?: CategoryType;
    visible: boolean;
  }>({ visible: false });
  const openNewCategoryModal = (parentId: string, categoryType: CategoryType) =>
    setNewCategoryModalVisible({ parentId, categoryType, visible: true });
  const closeNewCategoryModal = () =>
    setNewCategoryModalVisible({ visible: false });

  const [editCategoryModalVisible, setEditCategoryModalVisible] = useState<{
    categoryType?: CategoryType;
    category?: { title: string; id: string };
    visible: boolean;
  }>({ visible: false });
  const openEditCategoryModal = (
    category: { title: string; id: string },
    categoryType: CategoryType
  ) => setEditCategoryModalVisible({ category, categoryType, visible: true });
  const closeEditCategoryModal = () =>
    setEditCategoryModalVisible({ visible: false });

  const [deleteModalVisible, setDeleteModalVisible] = useState<{
    id?: string;
    key?: string;
    categoryType?: CategoryType;
    visible: boolean;
  }>({ visible: false });
  const openDeleteDialog = (
    id: string,
    key: string,
    categoryType: CategoryType
  ) => setDeleteModalVisible({ id, key, categoryType, visible: true });
  const closeDeleteModal = () =>
    setDeleteModalVisible({
      id: undefined,
      categoryType: undefined,
      visible: false,
    });

  const [isDeleteLoading, setIsDeleteLoading] = useState(false);

  const deleteCategory = async () => {
    const { categoryType, id, key } = deleteModalVisible;
    if (
      categoryType === undefined ||
      id === undefined ||
      (key === undefined && categoryType === "subCategory")
    )
      return;
    setIsDeleteLoading(true);
    const categoriesRepository = ServiceLocater.get<CategoriesRepository>(
      Services.CategoriesRepository
    );
    let res: Either<CategoriesFailure, null> = left(
      CategoriesFailure.serverFailure()
    );
    if (categoryType === "category") {
      res = await categoriesRepository.deleteCategory(id);
    } else if (categoryType === "subCategory") {
      const ids = key?.split("-");
      const subCategory = categories
        .find((generalCategory) => generalCategory.id.toString() === ids![0])
        ?.subCategories.find(
          (subCategory) => subCategory.id.toString() === ids![1]
        );
      if (subCategory !== undefined) {
        res = await categoriesRepository.deleteSubCategory(subCategory);
      }
    } else {
      const generalCategory = categories.find(
        (generalCategory) => generalCategory.id === id
      );
      if (generalCategory !== undefined) {
        res = await categoriesRepository.deleteGeneralCategory(generalCategory);
      }
    }
    setIsDeleteLoading(false);
    res.fold(
      (_) => {
        message.error(I18n.general.networkError);
      },
      (_) => {
        message.success(I18n.categories.deleteSuccess);
        closeDeleteModal();
        dispatch(loadCategories());
      }
    );
  };

  const renderTreeNode = (
    key: string,
    id: string,
    title: string,
    categoryType: CategoryType,
    children: React.ReactNode[]
  ) => {
    const isAddButton = String(key).startsWith("add");
    return (
      <Tree.TreeNode
        key={key}
        title={
          <Row
            align="middle"
            onClick={
              isAddButton
                ? (_) => {
                    const keys = key.split("-");
                    openNewCategoryModal(keys[keys.length - 1], categoryType);
                  }
                : undefined
            }
          >
            {isAddButton ? (
              <PlusOutlined
                style={{
                  marginLeft: 8,
                  color: "#40a9ff",
                  padding: "8px 8px 8px 0",
                }}
              />
            ) : (
              <Fragment />
            )}
            <Typography.Text
              style={
                isAddButton
                  ? { color: "#40a9ff", paddingRight: 8 }
                  : { paddingRight: 8 }
              }
            >
              {title}
            </Typography.Text>
            {isAddButton ? (
              <Fragment />
            ) : (
              <Fragment>
                <Button
                  type="link"
                  size="middle"
                  onClick={() =>
                    openEditCategoryModal({ id, title }, categoryType)
                  }
                  disabled={isDeleteLoading}
                >
                  <EditOutlined />
                </Button>
                <Button
                  type="link"
                  size="middle"
                  onClick={() => openDeleteDialog(id, key, categoryType)}
                  disabled={isDeleteLoading}
                  danger
                >
                  <DeleteOutlined />
                </Button>
              </Fragment>
            )}
          </Row>
        }
        isLeaf={children.length === 0}
        children={children}
      />
    );
  };

  const renderTreeLeaves = () => {
    return [
      ...categories.map((generalCategory) =>
        renderTreeNode(
          generalCategory.id,
          generalCategory.id,
          generalCategory.title,
          "generalCategory",
          [
            ...generalCategory.subCategories.map((subCategory) =>
              renderTreeNode(
                `${generalCategory.id}-${subCategory.id}`,
                subCategory.id,
                subCategory.title,
                "subCategory",
                [
                  ...subCategory.categories.map((category) =>
                    renderTreeNode(
                      `${generalCategory.id}-${subCategory.id}-${category.id}`,
                      category.id,
                      category.title,
                      "category",
                      []
                    )
                  ),
                  renderTreeNode(
                    `add-${generalCategory.id}-${subCategory.id}`,
                    `add-${generalCategory.id}-${subCategory.id}`,
                    "افزودن دسته‌بندی",
                    "category",
                    []
                  ),
                ]
              )
            ),
            renderTreeNode(
              `add-${generalCategory.id}`,
              `add-${generalCategory.id}`,
              "افزودن دسته‌بندی فرعی",
              "subCategory",
              []
            ),
          ]
        )
      ),
      renderTreeNode(
        "add",
        "add",
        "افزودن دسته‌بندی اصلی",
        "generalCategory",
        []
      ),
    ];
  };

  if (loading !== "succeeded")
    return (
      <Row
        justify="center"
        align="middle"
        style={{ width: "100%", height: 300 }}
      >
        <Spin spinning={loading === "pending"} />
      </Row>
    );
  return (
    <div>
      <Tree
        blockNode
        defaultExpandAll={true}
        style={{ padding: 24 }}
        selectable={false}
        checkable={false}
        switcherIcon={<DownOutlined />}
      >
        {renderTreeLeaves()}
      </Tree>
      <Modal
        title={I18n.discount.deleteConfirm}
        visible={deleteModalVisible.visible}
        closable={!isDeleteLoading}
        maskClosable={!isDeleteLoading}
        onCancel={isDeleteLoading ? undefined : closeDeleteModal}
        onOk={deleteCategory}
        confirmLoading={isDeleteLoading}
      />

      <NewCategoryModal
        visible={newCategoryModalVisible.visible}
        parentId={newCategoryModalVisible?.parentId}
        onClose={closeNewCategoryModal}
        categoryType={newCategoryModalVisible?.categoryType}
      />

      <EditCategoryModal
        visible={editCategoryModalVisible.visible}
        onClose={closeEditCategoryModal}
        category={editCategoryModalVisible?.category}
        categoryType={editCategoryModalVisible?.categoryType}
      />
    </div>
  );
}
